import type {
  Agent,
  Catalog,
  OrgDescriptor,
  OrgId,
  ProcessDoc,
  ProcessPatch,
  ProcessSummary,
  WizardReply,
  ReleaseActionInput,
} from '@wand/shared';
import type { OrgDataset } from '@wand/shared/dataset/org.js';
import type { Group } from '@wand/shared/dataset/groups.js';
import type { InboxItem } from '@wand/shared/dataset/inbox.js';
import type { SignalMetric } from '@wand/shared/dataset/signals.js';
import type { CredentialAction } from '@wand/shared/dataset/connector_credential.js';
import type { Connector } from '@wand/shared/dataset/connectors.js';
import type { ConnectorTrigger } from '@wand/shared/dataset/connector_triggers.js';
import type { IntegrationInsight } from '@wand/shared/dataset/integration_insights.js';
import type { ChannelDecisions, ChannelSend, ChannelSendResult } from '@wand/contracts';
import type { AuthState } from '../gateway/types.js';

/** Base URL for the BFF. Empty string => same origin (proxied in dev, nginx in prod). */
const BASE = import.meta.env.VITE_API_BASE ?? '';

type AnySchema = { parse(v: unknown): unknown };
type Contracts = typeof import('@wand/contracts');
let contractsPromise: Promise<Contracts> | null = null;

/**
 * Dev/test-only response validation against @wand/contracts (ADR-0002): a BFF
 * response that drifts from the contract logs loudly at the call site instead
 * of crashing later at some unrelated use-site. Production builds return
 * immediately — the dynamic import sits in a PROD-dead branch, so zod and the
 * schemas never enter the production bundle.
 */
async function devValidate<T>(value: T, pick: (c: Contracts) => AnySchema, what: string): Promise<T> {
  if (import.meta.env.PROD) return value;
  contractsPromise ??= import('@wand/contracts');
  const contracts = await contractsPromise;
  try {
    pick(contracts).parse(value);
  } catch (err) {
    console.error(`[contracts] ${what} response does not match its schema`, err, value);
  }
  return value;
}

/**
 * A failed request, carrying the status rather than burying it in a string.
 *
 * Callers were matching `/413/` against the message to tell "too large" from
 * anything else, which also matches a 413 appearing anywhere inside a server's
 * error body. The number belongs on the error.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly body: string,
  ) {
    super(`${status} ${statusText}: ${body}`);
    this.name = 'ApiError';
  }
}

async function apiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => res.statusText);
  return new ApiError(res.status, res.statusText, text);
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when there's actually a body — a bodyless
  // DELETE with content-type: application/json is rejected by Fastify.
  const res = await fetch(`${BASE}${path}`, {
    // Send the session cookie: in `oidc` mode the BFF authenticates every /api
    // request from it (ADR-0015). Same-origin in dev (Vite proxy) and in the
    // single-container build, so this only matters when VITE_API_BASE is set.
    credentials: 'include',
    headers: {
      ...(init?.body != null ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) throw await apiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Like `http`, but a 404 resolves to `undefined` (lookups that may miss). */
async function httpMaybe<T>(path: string, init?: RequestInit): Promise<T | undefined> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 404) return undefined;
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

export const api = {
  catalog: () => http<Catalog>('/api/catalog'),

  /* ---- identity (ADR-0015) ---- */
  /** The login flow is a browser redirect, so these are URLs, not fetches. */
  authLoginUrl: () => `${BASE}/api/auth/login`,
  authLogoutUrl: () => `${BASE}/api/auth/logout`,
  me: () => http<AuthState>('/api/auth/me').then((v) => devValidate(v, (c) => c.meResponse, 'me')),

  /* ---- org registry + per-org data ---- */
  listOrgs: () =>
    http<OrgDescriptor[]>('/api/orgs').then((v) =>
      devValidate(v, (c) => c.orgDescriptorSchema.array(), 'listOrgs'),
    ),
  getDataset: (org: OrgId) => http<OrgDataset>(`/api/orgs/${org}/dataset`),

  /* ---- the desk channel: the one real message this platform sends ---- */
  /** Body is a closed reference, never content — see `@wand/contracts/channel`. */
  channelSend: (org: OrgId, body: ChannelSend) =>
    http<ChannelSendResult>(`/api/orgs/${org}/channel/send`, json('POST', body)).then((v) =>
      devValidate(v, (c) => c.channelSendResultSchema, 'channelSend'),
    ),
  channelDecisions: (org: OrgId, since?: string) =>
    http<ChannelDecisions>(
      `/api/orgs/${org}/channel/decisions${since ? `?since=${encodeURIComponent(since)}` : ''}`,
    ).then((v) => devValidate(v, (c) => c.channelDecisionsSchema, 'channelDecisions')),

  createAgent: (org: OrgId, input: Omit<Agent, 'pk'>) =>
    http<Agent>(`/api/orgs/${org}/agents`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.agentSchema, 'createAgent'),
    ),
  patchAgent: (org: OrgId, pk: string, patch: Partial<Agent>) =>
    http<Agent>(`/api/orgs/${org}/agents/${pk}`, json('PATCH', patch)).then((v) =>
      devValidate(v, (c) => c.agentSchema, 'patchAgent'),
    ),
  deleteAgent: (org: OrgId, pk: string) =>
    http<void>(`/api/orgs/${org}/agents/${pk}`, { method: 'DELETE' }),

  createGroup: (org: OrgId, input: Omit<Group, 'pk'>) =>
    http<Group>(`/api/orgs/${org}/groups`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.groupSchema, 'createGroup'),
    ),
  patchGroup: (org: OrgId, pk: string, patch: Partial<Omit<Group, 'pk'>>) =>
    http<Group>(`/api/orgs/${org}/groups/${pk}`, json('PATCH', patch)).then((v) =>
      devValidate(v, (c) => c.groupSchema, 'patchGroup'),
    ),

  /* ---- inbox actions (ADR-0009) ---- */
  resolveInboxItem: (org: OrgId, id: string, actionId: string) =>
    http<InboxItem>(`/api/orgs/${org}/inbox/${id}/resolve`, json('POST', { actionId })).then((v) =>
      devValidate(v, (c) => c.inboxItemSchema, 'resolveInboxItem'),
    ),
  replyInboxItem: (org: OrgId, id: string, text: string) =>
    http<InboxItem>(`/api/orgs/${org}/inbox/${id}/reply`, json('POST', { text })).then((v) =>
      devValidate(v, (c) => c.inboxItemSchema, 'replyInboxItem'),
    ),
  askInboxItem: (org: OrgId, id: string, text: string) =>
    http<InboxItem>(`/api/orgs/${org}/inbox/${id}/ask`, json('POST', { text })).then((v) =>
      devValidate(v, (c) => c.inboxItemSchema, 'askInboxItem'),
    ),

  createMetric: (org: OrgId, input: Omit<SignalMetric, 'id'> & { id?: string }) =>
    http<SignalMetric>(`/api/orgs/${org}/metrics`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.signalMetricSchema, 'createMetric'),
    ),

  /* ---- integration hub: connector mutations (ADR-0014) ---- */
  createConnector: (org: OrgId, input: unknown) =>
    http<Connector>(`/api/orgs/${org}/connectors`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.connectorSchema, 'createConnector'),
    ),
  patchConnector: (org: OrgId, id: string, patch: Partial<Connector>) =>
    http<Connector>(`/api/orgs/${org}/connectors/${id}`, json('PATCH', patch)).then((v) =>
      devValidate(v, (c) => c.connectorSchema, 'patchConnector'),
    ),
  connectorCredential: (org: OrgId, id: string, action: CredentialAction) =>
    http<Connector>(`/api/orgs/${org}/connectors/${id}/credential`, json('POST', action)),
  setConnectorPaused: (org: OrgId, id: string, paused: boolean) =>
    http<Connector>(
      `/api/orgs/${org}/connectors/${id}/${paused ? 'pause' : 'resume'}`,
      json('POST', {}),
    ),
  deleteConnector: (org: OrgId, id: string) =>
    http<void>(`/api/orgs/${org}/connectors/${id}`, { method: 'DELETE' }),
  connectorVersionAction: (org: OrgId, id: string, body: unknown) =>
    http<Connector>(`/api/orgs/${org}/connectors/${id}/version`, json('POST', body)),
  resolveConnectorDrift: (org: OrgId, id: string) =>
    http<Connector>(`/api/orgs/${org}/connectors/${id}/resolve-drift`, json('POST', {})),
  runDriftScan: (org: OrgId) =>
    http<Connector[]>(`/api/orgs/${org}/connectors/drift-scan`, json('POST', {})),
  resolveInsight: (org: OrgId, id: string) =>
    http<IntegrationInsight>(`/api/orgs/${org}/insights/${id}/resolve`, json('POST', {})),
  connectorApproval: (org: OrgId, id: string, body: unknown) =>
    http<Connector | undefined>(`/api/orgs/${org}/connectors/${id}/approval`, json('POST', body)).then(
      (v) => v ?? null,
    ),
  upsertConnectorTrigger: (org: OrgId, connectorId: string, input: unknown) =>
    http<ConnectorTrigger>(`/api/orgs/${org}/connectors/${connectorId}/triggers`, json('PUT', input)),
  deleteConnectorTrigger: (org: OrgId, connectorId: string, id: string) =>
    http<void>(`/api/orgs/${org}/connectors/${connectorId}/triggers/${id}`, { method: 'DELETE' }),

  /* ---- editable process documents (the canvas) ---- */
  listProcesses: (org: OrgId) =>
    http<ProcessSummary[]>(`/api/orgs/${org}/process-docs`).then((v) =>
      devValidate(v, (c) => c.processSummarySchema.array(), 'listProcesses'),
    ),
  getProcess: (org: OrgId, id: string) => httpMaybe<ProcessDoc>(`/api/orgs/${org}/process-docs/${id}`),
  createProcess: (org: OrgId, name?: string, id?: string) =>
    http<ProcessDoc>(`/api/orgs/${org}/process-docs`, json('POST', { name, id })).then((v) =>
      devValidate(v, (c) => c.processDocSchema, 'createProcess'),
    ),
  releaseProcess: (org: OrgId, id: string, input: ReleaseActionInput) =>
    http<ProcessDoc>(`/api/orgs/${org}/process-docs/${id}/release`, json('POST', input)).then((v) =>
      devValidate(v, (c) => c.processDocSchema, 'releaseProcess'),
    ),
  saveProcess: (org: OrgId, id: string, patch: ProcessPatch) =>
    http<ProcessDoc>(`/api/orgs/${org}/process-docs/${id}`, json('PUT', patch)).then((v) =>
      devValidate(v, (c) => c.processDocSchema, 'saveProcess'),
    ),
  deleteProcess: (org: OrgId, id: string) =>
    http<void>(`/api/orgs/${org}/process-docs/${id}`, { method: 'DELETE' }),
  /**
   * Retire a process from the LIBRARY: its row, detail bundle and canvas doc.
   * `deleteProcess` above is the narrower one — it drops only the document a
   * canvas edits, which leaves the library card standing.
   */
  deleteLibraryProcess: (org: OrgId, pk: string) =>
    http<void>(`/api/orgs/${org}/processes/${pk}`, { method: 'DELETE' }),

  wizard: (message: string, processId?: string) =>
    http<WizardReply>('/api/wizard', json('POST', { message, processId })),
};
